import { FC, useMemo, useState } from 'react';
import { Grid, Typography, Box, CircularProgress, Alert, Chip, Card, CardContent, Divider, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useGuardianState } from '../../../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useGuardianLessonPlansHook } from '../../../../hooks/useGuardianAcademicHook';
import { useAcademicsFiltersContext } from '../../../../../academics/contexts/academicsFilters.context';
import PaginatedGrid from '../../../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ILessonPlanApiResponse, ELessonPlanStatus } from '../../../../../../../apis/academics/academicsLessonPlans.interfaces';

enum EViewType {
  GRID = 'grid',
  DETAILS = 'details',
}

const GuardianLessonPlansView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const { academicYear, grade } = useAcademicsFiltersContext();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedLessonPlan, setSelectedLessonPlan] = useState<ILessonPlanApiResponse | null>(null);
  const [viewType, setViewType] = useState<EViewType>(EViewType.GRID);

  const { data, isLoading, error } = useGuardianLessonPlansHook(
    selectedChildId || '',
    {
      page,
      limit: pageSize,
      ...(academicYear && { academicYear }),
      ...(grade && { grade }),
    }
  );

  const lessonPlans: ILessonPlanApiResponse[] = useMemo(() => data?.lessonPlans || [], [data?.lessonPlans]);
  const totalCount = data?.total || 0;

  const columns = useMemo<GridColDef[]>(() => [
    {
      field: 'title',
      headerName: translateText('guardian.academics.lessonPlans.columns.title'),
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight="medium">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'subject',
      headerName: translateText('guardian.academics.lessonPlans.columns.subject'),
      width: 150,
    },
    {
      field: 'teacher',
      headerName: translateText('guardian.academics.lessonPlans.columns.teacher'),
      width: 180,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const lessonPlan = params.row as ILessonPlanApiResponse;
        return (
          <Typography variant="body2">
            {(lessonPlan as ILessonPlanApiResponse).teacher?.name || '-'}
          </Typography>
        );
      },
    },
    {
      field: 'gradeLevel',
      headerName: translateText('guardian.academics.lessonPlans.columns.gradeLevel'),
      width: 120,
    },
    {
      field: 'academicYear',
      headerName: translateText('guardian.academics.lessonPlans.columns.academicYear'),
      width: 150,
    },
    {
      field: 'status',
      headerName: translateText('guardian.academics.lessonPlans.columns.status'),
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value as ELessonPlanStatus;
        const statusColors: Record<
          ELessonPlanStatus,
          'default' | 'success' | 'warning' | 'error' | 'info'
        > = {
          [ELessonPlanStatus.DRAFT]: 'default',
          [ELessonPlanStatus.IN_REVIEW]: 'info',
          [ELessonPlanStatus.APPROVED]: 'success',
          [ELessonPlanStatus.ACTIVE]: 'success',
          [ELessonPlanStatus.ARCHIVED]: 'default',
        };
        return (
          <Chip
            label={status}
            color={statusColors[status] || 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'totalDuration',
      headerName: translateText('guardian.academics.lessonPlans.columns.duration'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const duration = params.value as number;
        return duration ? `${duration} ${translateText('guardian.academics.lessonPlans.days')}` : '-';
      },
    },
  ], [translateText]);

  const rows = useMemo(() => {
    return lessonPlans.map((lessonPlan: ILessonPlanApiResponse) => ({
      ...lessonPlan, // Include full lesson plan object for detail view
    }));
  }, [lessonPlans]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  const handleRowClick = (params: any) => {
    const lessonPlan = lessonPlans.find((lp: ILessonPlanApiResponse) => lp.id === params.row.id);
    if (lessonPlan) {
      setSelectedLessonPlan(lessonPlan);
      setViewType(EViewType.DETAILS);
    }
  };

  const handleBackToGrid = () => {
    setViewType(EViewType.GRID);
    setSelectedLessonPlan(null);
  };

  const getStatusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    const statusColors: Record<
      ELessonPlanStatus,
      'default' | 'success' | 'warning' | 'error' | 'info'
    > = {
      [ELessonPlanStatus.DRAFT]: 'default',
      [ELessonPlanStatus.IN_REVIEW]: 'info',
      [ELessonPlanStatus.APPROVED]: 'success',
      [ELessonPlanStatus.ACTIVE]: 'success',
      [ELessonPlanStatus.ARCHIVED]: 'default',
    };
    return statusColors[status as ELessonPlanStatus] || 'default';
  };

  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-lesson-plans-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.selectChild')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-lesson-plans-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-lesson-plans-container">
        <Grid item xs={12}>
          <Alert severity="error">{translateText('common.error')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (viewType === EViewType.DETAILS && selectedLessonPlan) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-lesson-plans-container">
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToGrid}
              data-testid="back-to-grid-button"
            >
              {translateText('common.back')}
            </Button>
            <Typography variant="h6" data-testid="lesson-plan-detail-title">
              {selectedLessonPlan.title}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Card data-testid="lesson-plan-detail-card">
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translateText('guardian.academics.lessonPlans.columns.title')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLessonPlan.title}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translateText('guardian.academics.lessonPlans.columns.subject')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLessonPlan.subject || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translateText('guardian.academics.lessonPlans.columns.teacher')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLessonPlan.teacher?.name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translateText('guardian.academics.lessonPlans.columns.gradeLevel')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLessonPlan.gradeLevel || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translateText('guardian.academics.lessonPlans.columns.academicYear')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLessonPlan.academicYear || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translateText('guardian.academics.lessonPlans.columns.status')}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={selectedLessonPlan.status}
                      color={getStatusColor(selectedLessonPlan.status)}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {translateText('guardian.academics.lessonPlans.columns.duration')}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedLessonPlan.totalDuration
                      ? `${selectedLessonPlan.totalDuration} ${translateText('guardian.academics.lessonPlans.days')}`
                      : '-'}
                  </Typography>
                </Grid>
                {selectedLessonPlan.description && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {translateText('common.description')}
                    </Typography>
                    <Typography variant="body2">
                      {selectedLessonPlan.description}
                    </Typography>
                  </Grid>
                )}
                {selectedLessonPlan.board && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {translateText('guardian.academics.lessonPlans.columns.board')}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedLessonPlan.board}
                    </Typography>
                  </Grid>
                )}
                {selectedLessonPlan.units && selectedLessonPlan.units.length > 0 && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {translateText('guardian.academics.lessonPlans.columns.units')}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedLessonPlan.units.length}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-lesson-plans-container">
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2 }} data-testid="lesson-plans-title">
          {translateText('guardian.academics.lessonPlans.title')}
        </Typography>
      </Grid>
      {lessonPlans.length === 0 ? (
        <Grid item xs={12}>
          <Alert severity="info">
            {translateText('guardian.academics.lessonPlans.noLessonPlans')}
          </Alert>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <PaginatedGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            dataTestId="guardian-lesson-plans-grid"
            serverSide={true}
            totalCount={totalCount}
            currentPage={page}
            currentPageSize={pageSize}
            onPageChange={handlePageChange}
            onRowClick={handleRowClick}
            rowsPerPageOptions={[5, 10, 25, 50]}
            hideColumnsOnMobile={['gradeLevel', 'academicYear', 'status']}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default GuardianLessonPlansView;
